/**
 * Where our income comes from (§1.5), estimated from what the view already carries — the server
 * sends only the total (`incomePerMin`). Per-hex pop fill and radiation are 4-bit nibbles, so
 * the parts are close, not exact; the pop row takes the remainder so the rows add up to the total.
 *
 *   income = (stipend + perAreaW·ΣareaW[rad<0.5] + perPop·Σpop + Σcities cityPerLevel·L·fill·(1−rad)) × UNDERDOG
 *
 * A city's worth is its own term plus the pop its capacity bonus holds (cityMult: +cityMultCenter·L
 * on its hex, +cityMultRing·L on each owned neighbour); both are linear in L, so one level is worth
 * the city's income / L at today's fill.
 *
 * Every row is the source's own term; the underdog multiplier (a nation-wide bonus) is a row of its
 * own, so a city's figure is the same in its chip and in the panel and never jumps with the bonus.
 */
import { BALANCE, cityUpgradeCost, playerCapScale, type EntityId, type EntityView, type PlayerView, type WorldMap } from '@sa/sim';
import { nearestLandHex } from './geo';

const E = BALANCE.economy;
const P = BALANCE.pop;

export interface CityIncome {
  id: EntityId;
  level: number;
  /** credits per minute this city brings in now (its own term: without the underdog bonus) */
  perMin: number;
  /** what one more level adds per minute at today's fill (0 at max level) */
  nextLevel: number;
  /** minutes for the next upgrade to pay for itself (Infinity: never, null: max level) */
  paybackMin: number | null;
}

export interface IncomeBreakdown {
  /** the server's figure (the rows below, bonus included, sum to it) */
  total: number;
  stipend: number;
  land: number;
  /** pop outside what the cities' capacity holds (the remainder) */
  pop: number;
  cities: number;
  /** 1, or the underdog multiplier while our land share is far behind the average */
  mult: number;
  /** what the underdog multiplier adds on top of the rows above (0 without it) */
  bonus: number;
  /** our finished cities, best first */
  perCity: CityIncome[];
}

/** The underdog multiplier (§1.5) from the 1 Hz area shares: alive = any land left. */
export function underdogMult(view: PlayerView): number {
  const alive = view.shares.filter((s) => s.areaShare > 0);
  if (!alive.length) return 1;
  const avg = alive.reduce((a, s) => a + s.areaShare, 0) / alive.length;
  return view.areaShare < E.underdogThreshold * avg ? E.underdogMult : 1;
}

/** Our capacity scale (the same region table and team multiplier the server picked). */
export function myCapScale(view: PlayerView, world: WorldMap): number {
  const me = view.players.find((p) => p.id === view.me);
  return me ? playerCapScale(world, view.players, me.regionId) : 1;
}

/** Income of one of our cities before the underdog bonus, for its current level. */
function cityBase(view: PlayerView, world: WorldMap, h: number, level: number, capScale: number): number {
  const fill = (x: number) => view.dens[x] / 15;
  const radF = (x: number) => 1 - P.radCapLoss * (view.rad[x] / 15);
  let held = fill(h) * world.capBase[h] * radF(h) * P.cityMultCenter;
  for (let k = 0; k < 6; k++) {
    const n = world.nbr[h * 6 + k];
    if (n >= 0 && view.owner[n] === view.me) held += fill(n) * world.capBase[n] * radF(n) * P.cityMultRing;
  }
  return level * (E.cityPerLevel * fill(h) * (1 - view.rad[h] / 15) + E.perPop * held * capScale);
}

function cityLevel(e: EntityView): number {
  return e.detail?.level ?? e.extra;
}

/** One finished city of ours: income now (its own term), one more level's worth and its payback. */
export function cityIncome(view: PlayerView, world: WorldMap, city: EntityView, capScale = myCapScale(view, world)): CityIncome {
  const level = cityLevel(city);
  const h = nearestLandHex(world, city.x, city.y);
  const perLevel = h >= 0 && level > 0 ? cityBase(view, world, h, level, capScale) / level : 0;
  const maxed = level >= BALANCE.city.maxLevel;
  return {
    id: city.id,
    level,
    perMin: perLevel * level,
    nextLevel: maxed ? 0 : perLevel,
    paybackMin: maxed ? null : perLevel > 0 ? cityUpgradeCost(level) / perLevel : Infinity,
  };
}

/** What a new city (L1) on our hex `h` would bring in at today's fill (the placement caption; its own term). */
export function newCityIncome(view: PlayerView, world: WorldMap, h: number): number {
  return cityBase(view, world, h, 1, myCapScale(view, world));
}

/** Our income split into stipend / land / pop / cities + the underdog bonus (rows sum to `view.incomePerMin`). */
export function incomeBreakdown(view: PlayerView, world: WorldMap): IncomeBreakdown {
  const mult = underdogMult(view);
  const capScale = myCapScale(view, world);
  let area = 0;
  const radMax = Math.round(E.areaRadMax * 15);
  for (let h = 0; h < world.landCount; h++) if (view.owner[h] === view.me && view.rad[h] < radMax) area += world.areaW[h];
  const perCity: CityIncome[] = [];
  for (const e of view.entities.values()) {
    if (e.kind !== 'city' || e.owner !== view.me || e.state === 'building') continue;
    perCity.push(cityIncome(view, world, e, capScale));
  }
  perCity.sort((a, b) => b.perMin - a.perMin || b.level - a.level || a.id - b.id);
  const total = view.incomePerMin;
  const base = total / mult;
  const stipend = E.stipend;
  const land = E.perAreaW * area;
  const cities = perCity.reduce((a, c) => a + c.perMin, 0);
  return { total, stipend, land, cities, pop: Math.max(0, base - stipend - land - cities), mult, bonus: total - base, perCity };
}
